import { useState } from "react";
import { useNavigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import {
  usePendingCompensations,
  useApproveCompensation,
  useRejectCompensation,
} from "../hooks/useDispatch";
import type { CompensationNoteSummaryDto } from "../services/dispatchApi";
import { PermissionGate } from "@/shared/components/PermissionGate";

const formatDate = (v?: string) => (v ? new Date(v).toLocaleDateString("en-KE") : "—");

function compensationStatusBadge(status: string) {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    Draft: "outline",
    Pending: "secondary",
    Approved: "default",
    Rejected: "destructive",
  };
  return <Badge variant={variants[status] ?? "outline"}>{status}</Badge>;
}

// ── Approve Dialog ────────────────────────────────────────────────────────
function ApproveDialog({
  note,
  open,
  onOpenChange,
}: {
  note: CompensationNoteSummaryDto;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const notification = useNotification();
  const confirm = useConfirm();
  const approve = useApproveCompensation();
  const [reviewNotes, setReviewNotes] = useState("");

  const handleSubmit = () => {
    confirm({
      title: "Approve compensation note?",
      description: `${note.compensationNoteNumber} (${note.affectedTrayCount} tray(s), ${note.customerName}) will be approved. The system will fill from reserve and/or issue a credit note.`,
      confirmLabel: "Approve",
      onConfirm: async () => {
        try {
          await approve.mutateAsync({
            id: note.id,
            data: { compensationNoteId: note.id, reviewNotes: reviewNotes || undefined },
          });
          notification.success("Compensation note approved");
          onOpenChange(false);
          setReviewNotes("");
        } catch (err) {
          notification.error("Failed to approve");
          throw err;
        }
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Approve — {note.compensationNoteNumber}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded bg-muted px-3 py-2 text-sm space-y-1">
            <p><span className="text-muted-foreground">Customer:</span> {note.customerName}</p>
            <p><span className="text-muted-foreground">Affected Trays:</span> {note.affectedTrayCount}</p>
            <p><span className="text-muted-foreground">Trigger:</span> {note.trigger}</p>
          </div>
          <p className="text-sm text-muted-foreground">
            The system will automatically determine resolution: fill from reserve, issue a credit note, or both.
          </p>
          <div className="space-y-1">
            <Label>Review Notes</Label>
            <Textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} placeholder="Optional comments…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={approve.isPending}>
            {approve.isPending ? "Approving…" : "Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Reject Dialog ─────────────────────────────────────────────────────────
function RejectDialog({
  note,
  open,
  onOpenChange,
}: {
  note: CompensationNoteSummaryDto;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const notification = useNotification();
  const reject = useRejectCompensation();
  const [rejectionReason, setRejectionReason] = useState("");

  const handleSubmit = async () => {
    if (!rejectionReason.trim()) { notification.error("Rejection reason is required"); return; }
    try {
      await reject.mutateAsync({
        id: note.id,
        data: { compensationNoteId: note.id, rejectionReason },
      });
      notification.success("Compensation note rejected");
      onOpenChange(false);
      setRejectionReason("");
    } catch {
      notification.error("Failed to reject");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject — {note.compensationNoteNumber}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Provide a reason for rejection. This will be communicated to the raiser.</p>
          <div className="space-y-1">
            <Label>Rejection Reason *</Label>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Why is this compensation being rejected?"
              rows={4}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={reject.isPending}>
            {reject.isPending ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export const CompensationNotesPage = () => {
  const navigate = useNavigate();
  const { data: pending = [], isLoading } = usePendingCompensations();
  const [approveTarget, setApproveTarget] = useState<CompensationNoteSummaryDto | null>(null);
  const [rejectTarget, setRejectTarget] = useState<CompensationNoteSummaryDto | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Compensation Notes</h1>
          <p className="text-sm text-muted-foreground">Review and approve shortfall compensation requests</p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>← Back to Dispatch</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pending Approval</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : pending.length === 0 ? (
            <p className="text-sm text-muted-foreground">No compensation notes pending approval.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Note No.</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Dispatch</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead className="text-right">Trays</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Raised</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((n) => (
                  <TableRow key={n.id}>
                    <TableCell className="font-mono text-xs">{n.compensationNoteNumber}</TableCell>
                    <TableCell className="font-mono text-xs">{n.orderNumber}</TableCell>
                    <TableCell>{n.customerName}</TableCell>
                    <TableCell className="font-mono text-xs">{n.dispatchNumber}</TableCell>
                    <TableCell>{n.trigger}</TableCell>
                    <TableCell className="text-right">{n.affectedTrayCount}</TableCell>
                    <TableCell>{compensationStatusBadge(n.status)}</TableCell>
                    <TableCell>{formatDate(n.raisedAt)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <PermissionGate permissions={["dispatch.approve_compensation"]}>
                          <Button size="sm" onClick={() => setApproveTarget(n)}>Approve</Button>
                          <Button size="sm" variant="destructive" onClick={() => setRejectTarget(n)}>Reject</Button>
                        </PermissionGate>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      {approveTarget && (
        <ApproveDialog
          note={approveTarget}
          open={!!approveTarget}
          onOpenChange={(v) => { if (!v) setApproveTarget(null); }}
        />
      )}
      {rejectTarget && (
        <RejectDialog
          note={rejectTarget}
          open={!!rejectTarget}
          onOpenChange={(v) => { if (!v) setRejectTarget(null); }}
        />
      )}
    </div>
  );
};
